<template>
  <section>
    <div v-show="isValidDealerCode && !isLoading" class="hero">
      <div class="hero-body container box has-background-white-bis">
        <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage" id="error-container"></error-display-component>
        <div v-if="creditApplicationCompleted" class="credit-app is-success is-flex">
          <div>
            <i class="fas fa-check-circle is-success fa-2x"></i>
          </div>
          <div class="ml-20 has-text-left">
            <span class="is-size-5 has-text-weight-semibold">Application Completed!</span><br />
            Your credit application has already been completed and submitted for processing.
          </div>
        </div>
        <div class="steps" id="stepsDemo">
          <div class="step-item is-active is-success">
            <div class="step-marker">1</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Customer" class="fal fa-user fa-lg"></i>
                <i title="Vehicle" class="fal fa-car-alt fa-sx"></i>
              </p>
              <p class="step-title" v-else>CUSTOMER &amp; VEHICLE</p>
            </div>
          </div>
          <div v-show="showCoAppStep" class="step-item">
            <div class="step-marker">{{ coAppInfoStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Residence" class="fal fa-user-friends fa-lg"></i>
              </p>
              <p class="step-title" v-else>CO-CUSTOMER</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ coAppInfoStepCount + 1 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Residence" class="fal fa-home-alt fa-lg"></i>
              </p>
              <p class="step-title" v-else>RESIDENCE</p>
            </div>
          </div>
          <div v-show="showWorkStep" class="step-item">
            <div class="step-marker">{{ coAppInfoStepCount + 2 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Work" class="fal fa-building fa-lg"></i>
              </p>
              <p class="step-title" v-else>WORK</p>
            </div>
          </div>
          <div v-show="showSpouseStep" class="step-item">
            <div class="step-marker">{{ spouseInfoStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
              </p>
              <p class="step-title" v-else>SPOUSE</p>
            </div>
          </div>
          <div v-show="showSpouseResidence" class="step-item">
            <div class="step-marker">{{ spouseInfoStepCount + 1 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none; display: inline-block; white-space: nowrap;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
                <i title="Residence" class="fal fa-home-alt fa-sx"></i>
              </p>
              <p class="step-title" v-else>SPOUSE RESIDENCE</p>
            </div>
          </div>
          <div v-show="showSpouseWork" class="step-item">
            <div class="step-marker">{{ spouseWorkStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none; display: inline-block; white-space: nowrap;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
                <i title="Work" class="fal fa-building fa-sx"></i>
              </p>
              <p class="step-title" v-else>SPOUSE WORK</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ termsStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Terms" class="fal fa-file-signature fa-lg"></i>
              </p>
              <p class="step-title" v-else>TERMS</p>
            </div>
          </div>
          <div class="steps-content">
            <div class="step-content">
              <div class="has-text-centered credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Please complete your credit application
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form @submit.prevent="validateBeforeSubmit" class="has-text-centered is-active" data-vv-scope="step1">
                <section class="has-text-left box">
                  <div class="columns">
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Make<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.make"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.make') }"
                            data-vv-scope="step1"
                            data-vv-as="make"
                            placeholder="Select Make"
                            label="name"
                            track-by="id"
                            :options="makeOptions"
                            :searchable="true"
                            v-model="selectedMake">
                          </multiselect>
                          <span v-show="errors.has('step1.make')" class="help is-danger">
                            <i v-show="errors.has('step1.make')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.make') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step1.firstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step1.firstName') }" data-vv-scope="step1" data-vv-as="first name" type="text" placeholder="First Name" v-model="currentCustomer.firstName">
                          <span v-show="errors.has('step1.firstName')" class="help is-danger">
                            <i v-show="errors.has('step1.firstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.firstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step1.lastName" v-validate="{ 'required': true, 'max': 25 }" :class="{ 'input': true, 'is-danger': errors.has('step1.lastName') }" data-vv-scope="step1" data-vv-as="last name" type="text" placeholder="Last Name" v-model="currentCustomer.lastName">
                          <span v-show="errors.has('step1.lastName')" class="help is-danger">
                            <i v-show="errors.has('step1.lastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.lastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step1.dateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step1.dateOfBirth') }"
                            data-vv-scope="step1"
                            data-vv-as="date of birth"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step1.dateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step1.dateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.dateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field" v-if="currentSsn && currentSsn !== ''">
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input v-if="canEditSsn"
                            name="step1.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step1.ssn') }"
                            data-vv-scope="step1"
                            type="text"
                            v-model="currentCustomer.ssn"
                            v-mask="'###-##-####'">
                          <span v-else class="field is-size-7">{{ currentSsn }}</span>
                          <span v-show="errors.has('step1.ssn')" class="help is-danger">
                            <i v-show="errors.has('step1.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.ssn') }}
                          </span>
                        </div>
                        <div class="control is-size-7">
                          <b-checkbox
                            :native-value="false"
                            name="chkEditSsn"
                            type="is-info"
                            v-model="canEditSsn">
                            Edit SSN
                          </b-checkbox>
                        </div>
                      </div>
                      <div class="field" v-else>
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input
                            name="step1.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step1.ssn') }"
                            data-vv-scope="step1"
                            type="text"
                            v-model="currentCustomer.ssn"
                            placeholder="000-00-0000"
                            v-mask="'###-##-####'">
                          <span v-show="errors.has('step1.ssn')" class="help is-danger">
                            <i v-show="errors.has('step1.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.ssn') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="email" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step1.email') }" data-vv-scope="step1" data-vv-as="email" type="email" placeholder="Email" v-model="currentCustomer.email">
                          <span v-show="errors.has('step1.email')" class="help is-danger">
                            <i v-show="errors.has('step1.email')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.email') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="home" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step1.home') }" data-vv-scope="step1" data-vv-as="home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.home">
                          <span v-show="errors.has('step1.home')" class="help is-danger">
                            <i v-show="errors.has('step1.home')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.home') }}
                          </span>
                          <!-- <div v-show="!isHomePhoneValid.valid" v-validate="!isHomePhoneValid.valid" class="help is-danger">
                            <input type="hidden" data-vv-as="home" name="step1.home" id="home" v-model="isHomePhoneValid.errorCode" data-vv-scope="step1"/>
                            <i class="fas fa-exclamation-triangle"></i>{{ isHomePhoneValid.errorMessage }}
                          </div> -->
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                          <pop-fill-control title="Click to restore saved phone number" :fillableValue="originalHomePhone" :onFillAction="onRestoreHomePhone"></pop-fill-control>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="mobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step1.mobile') }" data-vv-scope="step1" data-vv-as="mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.mobile">
                          <span v-show="errors.has('step1.mobile')" class="help is-danger">
                            <i v-show="errors.has('step1.mobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.mobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">    <!-- disable if co customer is spouse -->
                        <label class="label">Marital Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.maritalStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.maritalStatus') }"
                            data-vv-scope="step1"
                            data-vv-as="marital status"
                            placeholder="Select Marital Status"
                            label="name"
                            track-by="id"
                            :options="definedTypes.customerMaritalStatus.options"
                            :searchable="true"
                            v-model="selectedMaritalStatus"
                            :disabled="isCoApplicantSpouse">
                          </multiselect>
                          <span v-show="errors.has('step1.maritalStatus')" class="help is-danger">
                            <i v-show="errors.has('step1.maritalStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.maritalStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Employment Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.employmentStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.employmentStatus') }"
                            data-vv-scope="step1"
                            data-vv-as="employment status"
                            placeholder="Select Employment Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.employmentStatus.options"
                            :searchable="true"
                            v-model="selectedEmploymentStatus">
                          </multiselect>
                          <span v-show="errors.has('step1.employmentStatus')" class="help is-danger">
                            <i v-show="errors.has('step1.employmentStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.employmentStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Co-Applicant?<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.relationshipStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.relationshipStatus') }"
                            data-vv-scope="step1"
                            data-vv-as="co-applicant relationship status"
                            placeholder="Select Co-Applicant Relationship Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.relationshipStatus.options"
                            :searchable="true"
                            v-model="selectedRelationshipStatus">
                          </multiselect>
                          <span v-show="errors.has('step1.relationshipStatus')" class="help is-danger">
                            <i v-show="errors.has('step1.relationshipStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.relationshipStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="isPrimaryPhoneAreaCodeValid.errorCode === 0 || isPrimaryMobilePhoneAreaCodeValid.errorCode === 0" class="has-text-centered" data-vv-scope="step1">
                <section class="has-text-left">
                  <div v-show="errors.has('step1.homeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="homeAreaCode" name="step1.homeAreaCode" id="homeAreaCode" v-validate="'required|min_value:1'" v-model="isPrimaryPhoneAreaCodeValid.errorCode" data-vv-scope="step1"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step1.mobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="mobileAreaCode" name="step1.mobileAreaCode" id="mobileAreaCode" v-validate="'required|min_value:1'" v-model="isPrimaryMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step1"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showCoAppStep" class="step-content">
              <div v-if="showCoAppStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Co-Applicant Details
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showCoAppStep" class="has-text-centered" data-vv-scope="step2">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step2.coFirstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coFirstName') }" data-vv-scope="step2" data-vv-as="co-applicant first name" type="text" placeholder="First Name" v-model="currentCustomer.coApplicant.firstName">
                          <span v-show="errors.has('step2.coFirstName')" class="help is-danger">
                            <i v-show="errors.has('step2.coFirstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coFirstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step2.coLastName" v-validate="{ 'required': true, 'max':25 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coLastName') }" data-vv-scope="step2" data-vv-as="co-applicant last name" type="text" placeholder="Last Name" v-model="currentCustomer.coApplicant.lastName">
                          <span v-show="errors.has('step2.coLastName')" class="help is-danger">
                            <i v-show="errors.has('step2.coLastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coLastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="coEmail" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coEmail') }" data-vv-scope="step2" data-vv-as="co-applicant email" type="email" placeholder="Email" v-model="currentCustomer.coApplicant.email">
                          <span v-show="errors.has('step2.coEmail')" class="help is-danger">
                            <i v-show="errors.has('step2.coEmail')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coEmail') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step2.coDateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step2.dateOfBirth') }"
                            data-vv-scope="step2"
                            data-vv-as="date of birth"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.coApplicant.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step2.coDateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step2.coDateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coDateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="coHome" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coHome') }" data-vv-scope="step2" data-vv-as="co-applicant home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.coApplicant.home">
                          <span v-show="errors.has('step2.coHome')" class="help is-danger">
                            <i v-show="errors.has('step2.coHome')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coHome') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="coMobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coMobile') }" data-vv-scope="step2" data-vv-as="co-applicant mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.coApplicant.mobile">
                          <span v-show="errors.has('step2.coMobile')" class="help is-danger">
                            <i v-show="errors.has('step2.coMobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coMobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="showCoAppStep && (isCoAppPhoneAreaCodeValid.errorCode === 0 || isCoAppMobilePhoneAreaCodeValid.errorCode === 0)" class="has-text-centered" data-vv-scope="step2">
                <section class="has-text-left">
                  <div v-show="errors.has('step2.coAppHomeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="coAppHomeAreaCode" name="step2.coAppHomeAreaCode" id="coAppHomeAreaCode" v-validate="'required|min_value:1'" v-model="isCoAppPhoneAreaCodeValid.errorCode" data-vv-scope="step2"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isCoAppPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step2.coAppMobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="coAppMobileAreaCode" name="step2.coAppMobileAreaCode" id="coAppMobileAreaCode" v-validate="'required|min_value:1'" v-model="isCoAppMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step2"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isCoAppMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide 3 Years of Residence History - Most recent first
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form class="has-text-centered" data-vv-scope="step3">
                <div v-show="errors.has('step3.primaryAddress')" class="notification is-warning">
                    <input type="hidden" data-vv-as="primaryAddress" name="step3.primaryAddress" id="primaryAddress" v-validate="'required|min_value:1'" v-model="isPrimaryAddressValid.errorCode" data-vv-scope="step3"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryAddressValid.errorMessage }}
                </div>
                <section class="has-text-left">
                  <div v-for="(address, index) in addresses" :key="address.id">
                    <address-capture v-model="addresses[index]" :addressIndex="index" :useValidator="thisValidator" :originalAddressList="previousCustomerAddresses" v-on:primary-address-set="onAutoSelectPrimaryAddress" v-on:unselect-other="onUnselectNonPrimary" v-on:on-remove-address="removeAddress" scope="step3" :required="true"></address-capture>
                    <br />
                  </div>
                  <div v-show="errors.has('step3.residenceTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="residenceTotalMonths" name="step3.residenceTotalMonths" id="residenceTotalMonths" v-validate="'required|min_value:1'" v-model="isResidenceMonthsValid.errorCode" data-vv-scope="step3"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isResidenceMonthsValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addAddress({})">Add</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showWorkStep" class="step-content">
              <div v-if="showWorkStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    {{ workHistoryHeading }} - {{ workHistorySubHeading }}
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showWorkStep" class="has-text-centered" data-vv-scope="step4">
                <section class="has-text-left">
                  <div v-for="(history, index) in workHistory" :key="history.id">
                    <work-history-capture v-model="workHistory[index]" :employmentStatusType="selectedEmploymentStatus ? selectedEmploymentStatus.id : 0" scope="step4" :required="true"></work-history-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step4.workHistoryTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryTotalMonths" name="step4.workHistoryTotalMonths" id="workHistoryTotalMonths" v-validate="'required|min_value:1'" v-model="isWorkHistoryMonthsValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryMonthsValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step4.workHistoryEmployerName')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryEmployerName" name="step4.workHistoryEmployerName" id="workHistoryEmployerName" v-validate="'required|min_value:1'" v-model="isWorkHistoryEmployerValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryEmployerValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step4.workHistoryOfficeNumber')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryOfficeNumber" name="step4.workHistoryOfficeNumber" id="workHistoryOfficeNumber" v-validate="'required|min_value:1'" v-model="isWorkHistoryPhoneAreaCodeValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addWorkHistory({})">Add</b-button>
                    <b-button icon-left="fa fa-lg fa-minus-circle" class="customButtons is-danger" type="button" inverted v-if="showRemoveWorkHistory" @click="removeWorkHistory({})">Remove</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseStep" class="step-content">
              <div v-if="showSpouseStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Spouse Details
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseStep" class="has-text-centered" data-vv-scope="step5">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step5.firstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step5.firstName') }" data-vv-scope="step5" data-vv-as="first name" type="text" placeholder="First Name" v-model="currentCustomer.spouse.firstName">
                          <span v-show="errors.has('step5.firstName')" class="help is-danger">
                            <i v-show="errors.has('step5.firstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.firstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step5.lastName" v-validate="{ 'required': true, 'max': 25 }" :class="{ 'input': true, 'is-danger': errors.has('step5.lastName') }" data-vv-scope="step5" data-vv-as="last name" type="text" placeholder="Last Name" v-model="currentCustomer.spouse.lastName">
                          <span v-show="errors.has('step5.lastName')" class="help is-danger">
                            <i v-show="errors.has('step5.lastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.lastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step5.sDateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step5.sDateOfBirth') }"
                            data-vv-scope="step5"
                            data-vv-as="date of birth"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.spouse.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step5.sDateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step5.sDateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.sDateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field" v-if="currentSpouseSsn && currentSpouseSsn !== ''">
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input v-if="canEditSpouseSsn"
                            name="step5.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step5.ssn') }"
                            data-vv-scope="step5"
                            type="text"
                            v-model="currentCustomer.spouse.ssn"
                            v-mask="'###-##-####'">
                          <span v-else class="field is-size-7">{{ currentSpouseSsn }}</span>
                          <span v-show="errors.has('step5.ssn')" class="help is-danger">
                            <i v-show="errors.has('step5.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.ssn') }}
                          </span>
                        </div>
                        <div class="field is-size-7 is-pulled-left mt-10">
                          <b-checkbox
                            :native-value="false"
                            name="chkEditSsn"
                            type="is-info"
                            v-model="canEditSpouseSsn">
                            edit SSN
                          </b-checkbox>
                        </div>
                      </div>
                      <div class="field" v-else>
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input
                            name="step5.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step5.ssn') }"
                            data-vv-scope="step5"
                            type="text"
                            v-model="currentCustomer.spouse.ssn"
                            placeholder="000-00-0000"
                            v-mask="'###-##-####'">
                          <span v-show="errors.has('step5.ssn')" class="help is-danger">
                            <i v-show="errors.has('step5.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.ssn') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="email" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step5.email') }" data-vv-scope="step5" data-vv-as="email" type="email" placeholder="Email" v-model="currentCustomer.spouse.email">
                          <span v-show="errors.has('step5.email')" class="help is-danger">
                            <i v-show="errors.has('step5.email')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.email') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="home" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step5.home') }" data-vv-scope="step5" data-vv-as="home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.spouse.home">
                          <span v-show="errors.has('step5.home')" class="help is-danger">
                            <i v-show="errors.has('step5.home')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.home') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="mobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step5.mobile') }" data-vv-scope="step5" data-vv-as="mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.spouse.mobile">
                          <span v-show="errors.has('step5.mobile')" class="help is-danger">
                            <i v-show="errors.has('step5.mobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.mobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Employment Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step5.employmentStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step5.employmentStatus') }"
                            data-vv-scope="step5"
                            data-vv-as="employment status"
                            placeholder="Select Employment Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.employmentStatus.options"
                            :searchable="true"
                            v-model="selectedSpouseEmploymentStatus">
                          </multiselect>
                          <span v-show="errors.has('step5.employmentStatus')" class="help is-danger">
                            <i v-show="errors.has('step5.employmentStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.employmentStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="showSpouseStep && (isSpousePhoneAreaCodeValid.errorCode === 0 || isSpouseMobilePhoneAreaCodeValid.errorCode === 0)" class="has-text-centered" data-vv-scope="step5">
                <section class="has-text-left">
                  <div v-show="errors.has('step5.spouseHomeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseHomeAreaCode" name="step5.spouseHomeAreaCode" id="spouseHomeAreaCode" v-validate="'required|min_value:1'" v-model="isSpousePhoneAreaCodeValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpousePhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step5.spouseMobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseMobileAreaCode" name="step5.spouseMobileAreaCode" id="spouseMobileAreaCode" v-validate="'required|min_value:1'" v-model="isSpouseMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseResidence" class="step-content">
              <div v-if="showSpouseResidence" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide 3 Years of Residence History - Most recent first
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseResidence" class="has-text-centered" data-vv-scope="step6">
                <div v-show="errors.has('step6.spousePrimaryAddress')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spousePrimaryAddress" name="step6.spousePrimaryAddress" id="spousePrimaryAddress" v-validate="'required|min_value:1'" v-model="isSpousePrimaryAddressValid.errorCode" data-vv-scope="step6"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpousePrimaryAddressValid.errorMessage }}
                </div>
                <section class="has-text-left">
                  <div v-for="(address, index) in spouseAddresses" :key="address.id">
                    <address-capture v-model="spouseAddresses[index]" :useValidator="thisValidator" :isSpouse="true" v-on:primary-address-set="onAutoSelectSpousePrimaryAddress" v-on:unselect-other="onUnselectSpouseNonPrimary" v-on:on-remove-address="removeAddress" :addressIndex="index" scope="step6" :required="true"></address-capture>
                    <br />
                  </div>
                  <div v-show="errors.has('step6.spouseResidenceTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseResidenceTotalMonths" name="step6.spouseResidenceTotalMonths" id="spouseResidenceTotalMonths" v-validate="'required|min_value:1'" v-model="isSpouseResidenceMonthsValid.errorCode" data-vv-scope="step6"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseResidenceMonthsValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addAddress({ spouseAddress: true })">Add</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseWork" class="step-content">
              <div v-if="showSpouseWork" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    {{ spouseWorkHistoryHeading }} - {{ spouseWorkHistorySubHeading }}
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseWork" class="has-text-centered" data-vv-scope="step7">
                <section class="has-text-left">
                  <div v-for="(history, index) in spouseWorkHistory" :key="history.id">
                    <work-history-capture v-model="spouseWorkHistory[index]" :employmentStatusType="selectedSpouseEmploymentStatus ? selectedSpouseEmploymentStatus.id : 0" scope="step7" :required="true"></work-history-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step7.spouseWorkHistoryTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryTotalMonths" name="step7.spouseWorkHistoryTotalMonths" id="spouseWorkHistoryTotalMonths" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryMonthsValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryMonthsValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step7.spouseWorkHistoryEmployerName')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryEmployerName" name="step7.spouseWorkHistoryEmployerName" id="spouseWorkHistoryEmployerName" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryEmployerValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryEmployerValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step7.spouseWorkHistoryOfficeNumber')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryOfficeNumber" name="step7.spouseWorkHistoryOfficeNumber" id="spouseWorkHistoryOfficeNumber" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryPhoneAreaCodeValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addWorkHistory({ spouseWorkHistory: true })">Add</b-button>
                    <b-button icon-left="fa fa-lg fa-minus-circle" class="customButtons is-danger" type="button" inverted v-if="showRemoveSpouseWorkHistory" @click="removeWorkHistory({ spouseWorkHistory: true })">Remove</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content has-text-centered">
              <div class="box" v-observe-visibility="visibilityChanged">
                <div class="field notification is-aligned-left ml-20">
                  <terms-control ref="termsControl" :currentCustomer="currentCustomer"></terms-control>
                </div>
                <div class="field notification is-aligned-left ml-20" v-if="communityPropertyDisclosureRequired">
                  <property-disclosure-control ref="copControl" :currentCustomer="currentCustomer"></property-disclosure-control>
                </div>
                <div class="field notification is-aligned-left ml-20" v-if="hasCoApplicant">
                  <regulation-b-control ref="regBControl" :currentCustomer="currentCustomer"></regulation-b-control>
                </div>
              </div>
            </div>
          </div>

          <div class="steps-actions">
            <div class="steps-action">
              <b-button type="button" icon-left="chevron-left" data-nav="previous" class="is-info" :disabled="isSaving">Previous</b-button>
            </div>
            <div class="steps-action">
              <b-button type="button" icon-right="chevron-right" data-nav="next" class="next-step is-info" @click="validateStep">Next</b-button>
            </div>
            <div class="steps-action" v-if="termsVisible && !creditApplicationCompleted">
              <a class="button next-step is-success" type="submit" @click="validateBeforeSubmitAllScoped" :class="{'is-loading': isSaving}" :disabled="isSaving">Submit</a>
            </div>
          </div>
          <div class="container">
            <b-button type="button" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
          </div>
        </div>
      </div>
    </div>
    <customer-lookup-control ref="customer_lookup" :currentCustomer="currentCustomer" :useValidator="thisValidator" v-on:on-load-lead="onLoadLead" v-on:on-no-lead="onNoLead"></customer-lookup-control>
    <customer-lookup-control ref="coapp_lookup" :currentCustomer="currentCustomer.coApplicant" :useValidator="thisValidator" :isCoAppSearch="true" v-on:on-load-lead="onLoadCoApplicant" v-on:on-no-lead="onNoCoApplicant"></customer-lookup-control>
    <div v-show="!isValidDealerCode && isLoading" class="hero">
      <div class="hero-body container">
        <div class="container is-fluid">
          <div class="columns is-vcentered loadingContainer">
            <div class="column is-12 has-text-centered has-text-weight-semi-bold">
              <img src="@/assets/ajax-loader.gif">
              <div v-if="!loadingBaseData">Verifying your identity....</div>
              <div v-if="loadingBaseData">Loading base data....</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-show="!isValidDealerCode && !isLoading" class= "hero">
      <div class="hero-body container"></div>
    </div>
  </section>
</template>

<script>
import moment from 'moment'
import customerService from '@/services/customerService'
import customerMixin from '@/mixins/customer/index'
import bulmaSteps from 'bulma-steps/src/js/bulma-steps'
import customerLookupControl from '@/components/generic/CustomerLookupControl'
import PopFillControl from '@/components/generic/PopFillControl'

export default {
  name: 'LeadCreditApplication',
  inject: {
    $validator: '$validator'
  },
  props: {
    dealerCode: {
      type: String,
      default: ''
    }
  },
  components: {
    'customer-lookup-control': customerLookupControl,
    'pop-fill-control': PopFillControl
  },
  mixins: [customerMixin],
  data () {
    return {
      isMobile: window.innerWidth <= 700,
      selectedMake: null,
      availableMakes: [],
      isValidDealerCode: false,
      currentCustomer: {
        id: 0,
        dealerId: 0,
        type: 'Lead',
        status: 'Active',
        firstName: null,
        lastName: null,
        email: null,
        mobile: null,
        home: null,
        addresses: [],
        workHistory: [],
        ssn: null,
        dateOfBirth: null,
        spouseId: 0,
        spouse: {
          id: 0,
          dealerId: 0,
          type: 'Spouse',
          status: 'Active',
          firstName: null,
          lastName: null,
          email: null,
          mobile: null,
          home: null,
          addresses: [],
          workHistory: [],
          ssn: null,
          dateOfBirth: null,
          isPartial: false,
          isCreditTermsAccepted: false,
          maritalStatus: null,
          regulationB: false,
          communityPropertyDisclosure: false,
          employmentStatus: null,
          isCreditApplicationComplete: false,
          leadId: '00000000-0000-0000-0000-000000000000',
          agentUserId: '00000000-0000-0000-0000-000000000000',
          agentLocationId: 0,
          createdAt: null,
          modifiedAt: null
        },
        coApplicant: {
          id: 0,
          dealerId: 0,
          type: 'Individual',
          status: 'Active',
          firstName: null,
          lastName: null,
          email: null,
          mobile: null,
          home: null,
          dateOfBirth: null
        },
        isPartial: false,
        isCreditTermsAccepted: false,
        maritalStatus: null,
        regulationB: false,
        communityPropertyDisclosure: false,
        employmentStatus: null,
        isCreditApplicationComplete: false,
        leadId: '00000000-0000-0000-0000-000000000000',
        agentUserId: '00000000-0000-0000-0000-000000000000',
        agentLocationId: 0,
        make: 0,
        makeName: null,
        model: null,
        year: null,
        vinNo: null,
        createdAt: null,
        modifiedAt: null
      },
      canEditSsn: false,
      currentSsn: '',
      currentSpouseSsn: '',
      canEditSpouseSsn: false,
      originalHomePhone: '',
      previousCustomerAddresses: []
    }
  },
  computed: {
    makeOptions: function () {
      return this.availableMakes
    },
    termsStepCount: function () {
      let stepCount = 8

      if (!this.showCoAppStep) {
        stepCount--
      }

      if (!this.showWorkStep) {
        stepCount--
      }

      if (!this.showSpouseStep) {
        stepCount--
      }

      if (!this.showSpouseResidence) {
        stepCount--
      }

      if (!this.showSpouseWork) {
        stepCount--
      }

      return stepCount
    },
    availableScopes: function () {
      return ['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7', 'step8']
    }
  },
  methods: {
    fetchDealerId: function (currentDealerCode) {
      this.serverErrorMessage = ''
      this.loadingBaseData = true
      this.isLoading = true
      customerService.getDealer(currentDealerCode).then(response => {
        this.dealerId = response.dealerId
        if (!this.dealerId || this.dealerId === 0) {
          this.serverErrorMessage = 'Could not load dealer with code: ' + this.currentDealerCode
        }
        this.fetchMakes()
        this.isLoading = false
        // eslint-disable-next-line handle-callback-err
      }).catch((error) => {
        this.isLoading = false
        this.serverErrorMessage = 'Could not load dealer with code: ' + currentDealerCode
      })
    },
    onRestoreHomePhone: function () {
      this.currentCustomer.home = this.originalHomePhone
    },
    onLoadLead: function (customer) {
      this.currentCustomer = customer
      this.previousCustomerAddresses = []
      this.currentCustomer.addresses.forEach(function (address) {
        var config = {
          sectionId: this.addresses.length + 100,
          loadCustomerOnly: false
        }
        address.config = config
        const checkDate = moment.tz(new Date(), moment.tz.guess()).subtract(10, 'day').format('YYYY-MM-DD')
        const currentDate = moment.tz(new Date(), moment.tz.guess()).format('YYYY-MM-DD')
        const modifiedDate = moment(new Date(address.modifiedAt)).format('YYYY-MM-DD')
        if (modifiedDate >= checkDate && modifiedDate <= currentDate) {
          this.addresses.push(address)
        } else {
          this.previousCustomerAddresses.push(address)
        }
      }, this)

      this.workHistory = []
      this.currentCustomer.workHistory.forEach(function (history) {
        var config = {
          sectionId: this.workHistory.length + 100,
          loadCustomerOnly: false
        }
        history.config = config
        this.workHistory.push(history)
      }, this)

      if (this.currentCustomer.spouse) {
        this.spouseAddresses = []
        this.currentCustomer.spouse.addresses.forEach(function (address) {
          var config = {
            sectionId: this.spouseAddresses.length + 100,
            loadCustomerOnly: false,
            spouseAddress: true
          }
          address.config = config
          this.spouseAddresses.push(address)
        }, this)

        this.spouseWorkHistory = []
        this.currentCustomer.spouse.workHistory.forEach(function (history) {
          var config = {
            sectionId: this.spouseWorkHistory.length + 100,
            loadCustomerOnly: false,
            spouseWorkHistory: true
          }
          history.config = config
          this.spouseWorkHistory.push(history)
        }, this)

        this.currentSpouseSsn = this.currentCustomer.spouse.ssn
        this.currentCustomer.spouse.ssn = ''
      }

      this.selectedMaritalStatus = this.currentCustomer.maritalStatus
        ? this.definedTypes.customerMaritalStatus.options.find((x) => x.name === this.currentCustomer.maritalStatus)
        : this.definedTypes.customerMaritalStatus.options.find((x) => x.id === 0)
      this.selectedEmploymentStatus = this.currentCustomer.employmentStatus
        ? this.definedTypes.employmentStatus.options.find((x) => x.name === this.currentCustomer.employmentStatus)
        : this.definedTypes.employmentStatus.options.find((x) => x.id === 1)

      if (this.currentCustomer.spouse) {
        const employmentStatus = this.currentCustomer.spouse.employmentStatus
        this.selectedSpouseEmploymentStatus = employmentStatus
          ? this.definedTypes.employmentStatus.options.find((x) => x.name === employmentStatus)
          : this.definedTypes.employmentStatus.options.find((x) => x.id === 1)
      }

      this.originalHomePhone = this.currentCustomer.home && this.currentCustomer.home !== '' ? this.currentCustomer.home : ''
      this.currentCustomer.home = ''
      this.currentSsn = this.currentCustomer.ssn
      this.currentCustomer.ssn = ''
      this.$refs.customer_lookup.changeModalState(false)
    },
    onNoLead: function (customer) {
      this.currentCustomer.mobile = customer ? customer.mobile : ''
      this.currentCustomer.email = customer ? customer.email : ''
      this.currentCustomer.ssn = customer ? customer.ssn : ''
      this.currentCustomer.dateOfBirth = customer ? customer.dateOfBirth : ''
      this.$refs.customer_lookup.changeModalState(false)
    },
    onLoadCoApplicant: function (customer) {
      this.currentCustomer.coApplicant = customer
      this.$refs.coapp_lookup.changeModalState(false)
    },
    onNoCoApplicant: function (customer) {
      this.currentCustomer.coApplicant.mobile = customer ? customer.mobile : ''
      this.currentCustomer.coApplicant.email = customer ? customer.email : ''
      this.currentCustomer.coApplicant.ssn = customer ? customer.ssn : ''
      this.currentCustomer.coApplicant.dateOfBirth = customer ? customer.dateOfBirth : ''
      this.$refs.coapp_lookup.changeModalState(false)
    },
    initInputValues () {
      if (this.currentCustomer != null) {
        this.addAddress({})
        this.addAddress({ spouseAddress: true })
        this.addWorkHistory({})
        this.addWorkHistory({ spouseWorkHistory: true })

        this.selectedMaritalStatus = this.definedTypes.customerMaritalStatus.options.find((x) => x.id === 0)
        this.selectedEmploymentStatus = this.definedTypes.employmentStatus.options.find((x) => x.id === 0)
        this.selectedRelationshipStatus = this.definedTypes.relationshipStatus.options.find((x) => x.id === 5)
      }
    },
    fetchMakes: function () {
      customerService.fetchMakes(this.dealerId).then(response => {
        this.availableMakes = response.results
        this.isValidDealerCode = true
        if (this.availableMakes) {
          this.initInputValues()
        } else {
          this.serverErrorMessage = 'Could not load makes for dealer with code: ' + this.dealerCode
        }
        this.isLoading = false
        this.loadingBaseData = false
      }).catch((error) => {
        this.serverErrorMessage = error.message
      })
    },
    onSave: function () {
      if (!this.currentCustomer.isCreditTermsAccepted) {
        this.$refs.termsControl.showError()
        return
      }
      if (this.communityPropertyDisclosureRequired && !this.currentCustomer.communityPropertyDisclosure) {
        this.$refs.copControl.showError()
        return
      }
      if (this.hasCoApplicant && !this.currentCustomer.regulationB) {
        this.$refs.regBControl.showError()
        return
      }
      // A disclosure left checked for a state that no longer requires it is rejected by
      // DealerTrack. The control is hidden in that case, so clear it on the model here -
      // otherwise the stale value is reposted on every save with no way to undo it.
      if (!this.communityPropertyDisclosureRequired) {
        this.currentCustomer.communityPropertyDisclosure = false
      }
      // Same for Regulation B, but only for a sole applicant. hasCoApplicant is also false when
      // this customer IS the co-applicant, and that record has to keep its own acceptance - so
      // exclude it explicitly rather than relying on hasCoApplicant alone.
      if (!this.isCustomerCoApplicant && !this.hasCoApplicant) {
        this.currentCustomer.regulationB = false
      }
      var customerModel = {
        id: this.currentCustomer.id ? this.currentCustomer.id : 0,
        isCreate: true,
        subscriberId: 0,
        dealerId: this.dealerId,
        type: 'Lead',
        firstName: this.$lodash.startCase(this.currentCustomer.firstName),
        lastName: this.$lodash.startCase(this.currentCustomer.lastName),
        email: this.currentCustomer.email,
        mobile: this.currentCustomer.mobile,
        home: this.currentCustomer.home,
        addresses: [],
        workHistory: [],
        status: 'Active',
        ssn: this.currentCustomer.ssn !== '' ? this.currentCustomer.ssn : this.currentSsn,
        dateOfBirth: moment(this.currentCustomer.dateOfBirth).format('MM/DD/YYYY'),
        isCreditTermsAccepted: this.currentCustomer.isCreditTermsAccepted,
        maritalStatus: this.selectedMaritalStatus ? this.selectedMaritalStatus.name : null,
        employmentStatus: this.selectedEmploymentStatus ? this.selectedEmploymentStatus.name : null,
        relationshipType: this.selectedRelationshipStatus ? this.selectedRelationshipStatus.name : null,
        spouseId: 0,
        isContinueLater: this.currentCustomer.isContinueLater,
        communityPropertyDisclosure: this.currentCustomer.communityPropertyDisclosure,
        regulationB: this.currentCustomer.regulationB,
        url: window.location.href,
        makeName: this.currentCustomer.makeName,
        model: this.currentCustomer.model,
        year: this.currentCustomer.year,
        make: this.selectedMake ? this.selectedMake.id : null,
        vinNo: this.currentCustomer.vinNo,
        spouse: {},
        coApplicant: {},
        leadId: null,
        agentUserId: null,
        agentLocationId: 0,
        isSpouseCoApplicant: this.isCoApplicantSpouse,
        ageInMonths: this.calculateAgeInMonths
      }

      this.saveAddress(customerModel)
      this.saveWorkHistory(customerModel)

      if (this.spouseInfoRequired) {
        var spouseCustomerModel = {
          id: this.currentCustomer.spouse.id,
          isCreate: true,
          subscriberId: 0,
          dealerId: 0,
          type: 'Spouse',
          firstName: this.$lodash.startCase(this.currentCustomer.spouse.firstName),
          lastName: this.$lodash.startCase(this.currentCustomer.spouse.lastName),
          email: this.currentCustomer.spouse.email,
          mobile: this.currentCustomer.spouse.mobile,
          home: this.currentCustomer.spouse.home,
          addresses: [],
          workHistory: [],
          status: 'Active',
          ssn: this.currentCustomer.spouse.ssn,
          dateOfBirth: moment(this.currentCustomer.spouse.dateOfBirth).format('MM/DD/YYYY'),
          isCreditTermsAccepted: this.currentCustomer.isCreditTermsAccepted,
          maritalStatus: this.currentCustomer.selectedMaritalStatus ? this.currentCustomer.selectedMaritalStatus.name : null,
          employmentStatus: this.selectedSpouseEmploymentStatus.name,
          isContinueLater: this.currentCustomer.spouse.isContinueLater,
          ageInMonths: this.calculateSpouseAgeInMonths
        }
        customerModel.spouse = spouseCustomerModel
        this.saveSpouseAddress(customerModel.spouse)
        this.saveSpouseWorkHistory(customerModel.spouse)
      }

      if (this.hasCoApplicant && !this.isCoApplicantSpouse) {
        var coApplicantCustomerModel = {
          id: this.currentCustomer.coApplicant.id,
          dealerId: 0,
          type: 'Individual',
          status: 'Active',
          firstName: this.$lodash.startCase(this.currentCustomer.coApplicant.firstName),
          lastName: this.$lodash.startCase(this.currentCustomer.coApplicant.lastName),
          email: this.currentCustomer.coApplicant.email,
          mobile: this.currentCustomer.coApplicant.mobile,
          home: this.currentCustomer.coApplicant.home,
          dateOfBirth: moment(this.currentCustomer.coApplicant.dateOfBirth).format('MM/DD/YYYY')
        }
        customerModel.coApplicant = coApplicantCustomerModel
      }

      customerModel.customerId = null
      customerModel.leadId = null
      customerModel.agentUserId = null
      customerModel.agentLocationId = null

      this.isSaving = true
      this.serverErrorMessage = ''
      this.serverErrors = []
      customerService.save(customerModel).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
          this.$nextTick(function () {
            var elmnt = document.getElementById('main-page')
            elmnt.scrollTo(0, 0)
          })
        } else {
          this.$emit('on-save', response.customer)
          this.onChangesSaved()
          this.onComplete()
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
        this.$nextTick(function () {
          var elmnt = document.getElementById('main-page')
          elmnt.scrollTo(0, 0)
        })
      })
    },
    onComplete () {
      this.$router.push({ name: 'CreditApplicationComplete' })
    },
    resetCurrentCustomer () {
      this.currentCustomer = {
        id: 0,
        dealerId: 0,
        type: 'Lead',
        status: 'Active',
        firstName: null,
        lastName: null,
        email: null,
        mobile: null,
        home: null,
        addresses: [],
        workHistory: [],
        ssn: null,
        dateOfBirth: null,
        spouseId: 0,
        spouse: {
          id: 0,
          dealerId: 0,
          type: 'Spouse',
          status: 'Active',
          firstName: null,
          lastName: null,
          email: null,
          mobile: null,
          home: null,
          addresses: [],
          workHistory: [],
          ssn: null,
          dateOfBirth: null,
          isPartial: false,
          isCreditTermsAccepted: false,
          maritalStatus: null,
          regulationB: false,
          communityPropertyDisclosure: false,
          employmentStatus: null,
          isCreditApplicationComplete: false,
          leadId: '00000000-0000-0000-0000-000000000000',
          agentUserId: '00000000-0000-0000-0000-000000000000',
          agentLocationId: 0,
          createdAt: null,
          modifiedAt: null
        },
        coApplicant: {
          id: 0,
          dealerId: 0,
          type: 'Individual',
          status: 'Active',
          firstName: null,
          lastName: null,
          email: null,
          mobile: null,
          home: null,
          dateOfBirth: null
        },
        isPartial: false,
        isCreditTermsAccepted: false,
        maritalStatus: null,
        regulationB: false,
        communityPropertyDisclosure: false,
        employmentStatus: null,
        isCreditApplicationComplete: false,
        leadId: '00000000-0000-0000-0000-000000000000',
        agentUserId: '00000000-0000-0000-0000-000000000000',
        agentLocationId: 0,
        make: 0,
        makeName: null,
        model: null,
        year: null,
        vinNo: null,
        createdAt: null,
        modifiedAt: null
      }
    },
    async navigateToBusiness () {
      this.$router.push({ name: 'BusinessLeadCreditApplication', params: this.dealerCode })
    },
    onCoAppTypeChange: function (selectedRelationshipStatusChange) {
      this.selectedRelationshipStatus = selectedRelationshipStatusChange
    }
  },
  created () {
    this.$emit('is-home', false)
  },
  watch: {
    dealerCode: function (newValue, oldValue) {
      this.isValidDealerCode = false
      this.fetchDealerId(this.dealerCode)
    },
    selectedRelationshipStatus: function (newValue, oldValue) {
      if (newValue) {
        if (this.hasCoApplicant && !this.isCoApplicantSpouse) {
          this.$refs.coapp_lookup.changeModalState(true)
        }
      }
    }
  },
  mounted () {
    this.bulmaSteps = bulmaSteps.attach('.steps', {
      stepClickable: this.isMember,
      onValidateStep: (a, b, e) => {
        this.validateStep(e)
      },
      beforeNext: id => {
        var beforeNextResult = {}
        if (this.preventClick) {
          beforeNextResult.errors = ['Validation Failed']
          return beforeNextResult
        } else {
          switch (this.currentStep) {
            case 0:
              // beforeNextResult.errors = this.validateHomePhone()
              // if (beforeNextResult.errors.length === 0) {
              //   if (!this.showCoAppStep) {
              //     beforeNextResult.skipCount = 1
              //   }
              // }
              // return beforeNextResult
              if (!this.showCoAppStep) {
                beforeNextResult.skipCount = 1
              }
              return beforeNextResult
            case 2:
              beforeNextResult.errors = this.validateResidenceHistory()
              if (beforeNextResult.errors.length === 0 && !this.showWorkStep) {
                if (!this.showSpouseStep) {
                  beforeNextResult.skipCount = 4
                } else {
                  beforeNextResult.skipCount = 1
                }
              }
              return beforeNextResult
            case 3:
              beforeNextResult.errors = this.validateWorkHistory()
              if (beforeNextResult.errors.length === 0 && this.showWorkStep) {
                if (!this.showSpouseStep) {
                  beforeNextResult.skipCount = 3
                }
              }
              return beforeNextResult
            case 5:
              beforeNextResult.errors = this.validateSpouseResidenceHistory()
              if (beforeNextResult.errors.length === 0 && !this.showSpouseWork) {
                if (this.showSpouseStep) {
                  beforeNextResult.skipCount = 1
                }
              }
              return beforeNextResult
            case 6:
              beforeNextResult.errors = this.validateSpouseWorkHistory()
              return beforeNextResult
          }
          return beforeNextResult
        }
      },
      beforePrevious: id => {
        this.currentStep = id
        let skipCount = 0

        switch (this.currentStep) {
          case 2:
            if (!this.showCoAppStep) {
              skipCount = 1
            }
            return skipCount
          case 3:
            if (!this.showWorkStep) {
              skipCount = 1
            }
            return skipCount
          case 4:
            if (this.showSpouseStep && !this.showWorkStep) {
              skipCount = 1
            }
            return skipCount
          case 7:
            if (!this.showWorkStep) {
              if (!this.showSpouseStep) {
                skipCount = 4
              } else {
                if (!this.showSpouseWork) {
                  skipCount = 1
                }
              }
            } else {
              if (!this.showSpouseStep) {
                skipCount = 3
              } else {
                if (!this.showSpouseWork) {
                  skipCount = 1
                }
              }
            }
            return skipCount
        }
      },
      onShow: id => {
        var elmnt = document.getElementById('main-page')
        elmnt.scrollTo(0, 0)
        this.currentStep = id
      }
    })
    this.$refs.customer_lookup.changeModalState(true)
    this.resetCurrentCustomer()
    this.fetchDealerId(this.dealerCode)
    this.$emit('set-title-one', 'CUSTOMER CREDIT')
    this.$emit('set-title-two', 'APPLICATION')
  }
}

</script>

<style scoped>
  .customButtons {
    display: flex !important;
    flex-basis: 0 !important;
    flex-grow: 1 !important;
    margin: .5rem !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .customButtonContainer {
    display: flex !important;
    align-items: stretch !important;
    flex-basis: 100% !important;
  }

  .box {
    background-color: #f6f8fa;
  }

  .coApplicantContainer {
    background: #a6d8f7;
  }
</style>
